import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { OfferThumb } from "@/components/OfferCard";
import { Upload, Trash2, Loader2 } from "lucide-react";

const TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX = 2 * 1024 * 1024;

export default function OfferImageField({ value, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!TYPES.includes(f.type)) return setError("Only JPG, PNG or WebP images are allowed.");
    if (f.size > MAX) return setError("Image must be 2 MB or smaller.");
    setError("");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/offers/image", fd);
      onChange(data.image_url);
    } catch (err) {
      const msg = formatError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-1.5" data-testid="offer-image-field">
      <Label>Image (optional)</Label>
      <div className="flex items-center gap-3">
        <OfferThumb src={value} className="w-20 flex-shrink-0" />
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" className="rounded-full" disabled={busy}
                  onClick={() => inputRef.current?.click()} data-testid="offer-image-upload-btn">
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
            {value ? "Replace" : "Upload"}
          </Button>
          {value && (
            <Button type="button" variant="outline" size="sm" className="rounded-full text-red-700"
                    onClick={() => onChange("")} data-testid="offer-image-remove-btn">
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
            </Button>
          )}
        </div>
      </div>
      <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
             className="hidden" onChange={pick} data-testid="offer-image-input" />
      <p className="text-xs" style={{ color: error ? "#b91c1c" : "var(--text-muted)" }} data-testid="offer-image-error">
        {error || "JPG, PNG or WebP, up to 2 MB. The original file is kept; cards show it in a square frame."}
      </p>
    </div>
  );
}
